import { Badge } from "@/shared/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import { ChevronDown, ChevronRight, FileText } from "lucide-react";
import type {
  UiPaletteStep,
  UiTemplateItem,
} from "@/features/template-builder/queries";

type SectionPaletteGroupProps = {
  template: UiTemplateItem;
  steps: UiPaletteStep[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedStepKey: string | null;
  onSelectStep: (key: string) => void;
};

export function SectionPaletteGroup({
  template,
  steps,
  isOpen,
  onOpenChange,
  selectedStepKey,
  onSelectStep,
}: SectionPaletteGroupProps) {
  const groupLabel = template.isGlobal ? "Global" : "Tenant";

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={onOpenChange}
      className="data-[state=closed]:h-16 self-start overflow-hidden rounded-xl border bg-card shadow-sm"
    >
      <Card className="gap-0 border-0 py-0 shadow-none">
        <CardHeader className="h-full px-3 py-2.5">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-1 py-0.5 text-left transition-colors hover:bg-muted/50"
              aria-label={
                isOpen
                  ? "Collapse template sections"
                  : "Expand template sections"
              }
            >
              <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <FileText className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="truncate text-sm leading-tight">
                    {template.name}
                  </CardTitle>
                  <Badge
                    variant={template.isGlobal ? "secondary" : "outline"}
                    className="h-5 px-1.5 text-[11px]"
                  >
                    {groupLabel}
                  </Badge>
                </div>
                <CardDescription className="mt-0.5 text-xs">
                  {template.type}
                </CardDescription>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge variant="outline" className="h-5 px-1.5 text-[11px]">
                  {steps.length}
                </Badge>
                <div className="rounded-md border bg-background p-1 text-muted-foreground">
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </div>
              </div>
            </button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent className="border-t data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-2">
          <CardContent className="px-3 py-2.5">
            <div className="grid gap-1.5 sm:grid-cols-2">
              {steps.map((step) => {
                const key = `${step.templateId}:${step.id}`;
                const selected = key === selectedStepKey;

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onSelectStep(key)}
                    className={`w-full rounded-lg border px-2.5 py-2 text-left transition-colors hover:border-border hover:bg-muted/40 ${
                      selected
                        ? "border-primary/45 bg-primary/5"
                        : "border-muted-foreground/20 bg-background"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium leading-tight">
                            {step.name}
                          </span>
                          {selected ? (
                            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">
                              Selected
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 line-clamp-1 text-xs text-muted-foreground">
                          {step.description ?? (
                            <span className="italic text-muted-foreground/60">
                              No description
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-[11px] text-muted-foreground">
                        #{step.order}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
